<div class="p-6 max-w-2xl mx-auto">

  <!-- Breadcrumb -->
  <nav class="flex items-center gap-2 text-sm text-gray-500 mb-6">
    <a routerLink="/automation/workflows" class="hover:text-blue-600 transition-colors">Workflows</a>
    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
    </svg>
    <span class="text-gray-900 dark:text-white font-medium">
      {{ isEditing ? 'Editar Workflow' : 'Nuevo Workflow' }}
    </span>
  </nav>

  <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
        {{ isEditing ? 'Editar Workflow' : 'Nuevo Workflow' }}
      </h1>
      @if (!isEditing) {
        <button (click)="openTemplatesModal()"
                class="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
          </svg>
          Usar plantilla
        </button>
      }
    </div>

    @if (loading) {
      <div class="flex justify-center py-10 text-gray-400">
        <svg class="animate-spin w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
        Cargando...
      </div>
    } @else {
      <form [formGroup]="form" (ngSubmit)="guardar()" novalidate>

        @if (error) {
          <div class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {{ error }}
          </div>
        }

        <!-- Nombre -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre <span class="text-red-500">*</span>
          </label>
          <input type="text" formControlName="name" placeholder="Nombre del workflow"
                 [maxlength]="NAME_MAX_LENGTH"
                 [class]="'w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ' + (isFieldInvalid('name') ? 'border-red-400' : 'border-gray-200 dark:border-gray-600')"/>
          <div class="mt-1 flex items-start justify-between gap-3">
            @if (isFieldInvalid('name')) {
              <p class="text-xs text-red-500">{{ getFieldError('name') }}</p>
            } @else {
              <span></span>
            }
            <span class="shrink-0 text-xs"
                  [class]="nameLength >= NAME_MAX_LENGTH ? 'text-red-500' : 'text-gray-400 dark:text-gray-500'">
              {{ nameLength }}/{{ NAME_MAX_LENGTH }}
            </span>
          </div>
        </div>

        <!-- Descripción -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción</label>
          <textarea formControlName="description" rows="3" placeholder="Descripción del workflow (opcional)"
                    [maxlength]="DESCRIPTION_MAX_LENGTH"
                    class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"></textarea>
          <div class="mt-1 flex items-start justify-between gap-3">
            @if (isFieldInvalid('description')) {
              <p class="text-xs text-red-500">{{ getFieldError('description') }}</p>
            } @else {
              <span></span>
            }
            <span class="shrink-0 text-xs"
                  [class]="descriptionLength >= DESCRIPTION_MAX_LENGTH ? 'text-red-500' : 'text-gray-400 dark:text-gray-500'">
              {{ descriptionLength }}/{{ DESCRIPTION_MAX_LENGTH }}
            </span>
          </div>
        </div>

        <!-- Tipo de objeto + Carpeta -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tipo de objeto base <span class="text-red-500">*</span>
            </label>
            <select formControlName="objectType"
                    [class]="'w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ' + (isFieldInvalid('objectType') ? 'border-red-400' : 'border-gray-200 dark:border-gray-600')">
              <option [value]="null" disabled>Seleccionar tipo...</option>
              @for (opt of objectTypeOptions; track opt.value) {
                <option [value]="opt.value" [disabled]="opt.disabled">{{ opt.label }}</option>
              }
            </select>
            @if (isFieldInvalid('objectType')) {
              <p class="mt-1 text-xs text-red-500">{{ getFieldError('objectType') }}</p>
            }
            @if (lockedByActive) {
              <p class="mt-1 text-xs text-amber-600 dark:text-amber-400">
                No se puede cambiar con el workflow activo: dejaría huérfanas las inscripciones en curso.
              </p>
            }
            @if (form.get('objectType')?.value === 'TICKET') {
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                El workflow se disparará con los eventos del ticket (creado, actualizado, cambio de
                estado o prioridad, asignación) y podrá usar sus propiedades en condiciones y
                variables.
              </p>
            }
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Carpeta</label>
            <select formControlName="idFolder"
                    class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option [value]="null">Sin carpeta</option>
              @for (folder of folders; track folder.id) {
                <option [value]="folder.id">{{ folder.name }}</option>
              }
            </select>
          </div>
        </div>

        <!-- Etiquetas -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Etiquetas</label>
          <div class="flex flex-wrap gap-2 mb-2">
            @for (tag of tags; track tag) {
              <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                {{ tag }}
                <button type="button" (click)="removeTag(tag)" class="hover:text-blue-900 ml-0.5">&times;</button>
              </span>
            }
          </div>
          <div class="flex gap-2">
            <input type="text" [(ngModel)]="tagInput" [ngModelOptions]="{standalone: true}"
                   placeholder="Agregar etiqueta..."
                   (keydown.enter)="addTagOnEnter($event)"
                   class="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"/>
            <button type="button" (click)="addTag()"
                    class="px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
              Agregar
            </button>
          </div>
        </div>

        <!-- Responsable -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Responsable</label>
          <app-entity-select
            [value]="form.value.idOwnerUser"
            (valueChange)="form.patchValue({ idOwnerUser: $event })"
            (selected)="onOwnerSelected($event)"
            [displayText]="ownerName"
            [searchFn]="userSearch" [labelFn]="userLabel" [sublabelFn]="userSublabel"
            [allowClear]="false"
            placeholder="Buscar usuario por nombre o correo…"></app-entity-select>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Usuario responsable del workflow. Por defecto eres tú.
          </p>
        </div>

        <!-- Configuración avanzada (solo en edición — inc 1) -->
        @if (isEditing) {
        <div class="mb-4 border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
          <button type="button" (click)="showAdvanced = !showAdvanced"
                  class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">Configuración avanzada</span>
            <svg class="w-4 h-4 text-gray-400 transition-transform" [class.rotate-180]="showAdvanced"
                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
        </div>
        <div [class.hidden]="!showAdvanced">

        <!--
          INC_CRM_020 (6) inc15 — el workflow activo ya no bloquea la edición entera. Este aviso
          explica QUÉ queda congelado y por qué, en vez del antiguo "Solo se pueden editar
          workflows en estado Borrador o Pausado" que rebotaba hasta un cambio de descripción.
        -->
        @if (lockedByActive) {
          <div class="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
            <p class="text-sm font-medium text-amber-800 dark:text-amber-300">Workflow activo</p>
            <p class="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              Puedes editar el nombre, la descripción, la carpeta, las etiquetas y el responsable sin
              detener nada. La configuración de esta sección y el tipo de objeto base afectan a las
              inscripciones en curso, así que para cambiarlos hay que pausar el workflow primero.
            </p>
          </div>
        }

        <!-- 1. Inscripción y re-inscripción -->
        <div class="mb-4 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-100 dark:border-gray-600">
          <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Inscripción y re-inscripción</h3>

          <label class="flex items-start gap-2 cursor-pointer mb-1">
            <input type="checkbox" formControlName="allowReEnrollment"
                   class="w-4 h-4 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
            <span class="text-sm text-gray-700 dark:text-gray-300">
              @if (f['allowReEnrollment'].value) {
                <strong>Permitir re-inscripción:</strong> el registro podrá volver a entrar después de completar o salir del workflow.
              } @else {
                <strong>Inscripción única:</strong> el registro solo podrá entrar una vez a este workflow.
              }
            </span>
          </label>
          <p class="text-xs text-gray-400 dark:text-gray-500 ml-6 mb-3">
            @if (f['allowReEnrollment'].value) {
              Los registros que ya completaron o salieron del workflow podrán volver a inscribirse.
            } @else {
              Los registros que ya estuvieron inscritos históricamente no volverán a entrar aunque cumplan el criterio de activación.
            }
          </p>

          @if (f['allowReEnrollment'].value) {
            <div class="ml-6 mb-3 space-y-1.5">
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Inicio de re-inscripción</p>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" formControlName="reEnrollmentStartMode" value="FROM_START"
                       class="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500"/>
                <span class="text-sm text-gray-700 dark:text-gray-300">Desde el inicio del workflow</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" formControlName="reEnrollmentStartMode" value="FROM_NODE"
                       class="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500"/>
                <span class="text-sm text-gray-700 dark:text-gray-300">Desde un paso específico</span>
              </label>

              @if (f['reEnrollmentStartMode'].value === 'FROM_NODE') {
                <div class="ml-6 mt-2">
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Paso de inicio <span class="text-red-500">*</span>
                  </label>

                  @if (!isEditing) {
                    <p class="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800">
                      Guarda el workflow primero y construye el flujo para poder seleccionar un paso de inicio.
                    </p>
                  } @else if (nodesLoading) {
                    <p class="text-xs text-gray-400 flex items-center gap-1">
                      <svg class="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                      </svg>
                      Cargando pasos del workflow...
                    </p>
                  } @else if (availableNodes.length > 0) {
                    <select formControlName="reEnrollmentStartNodeId"
                            [class]="'w-full sm:w-80 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ' + (isFieldInvalid('reEnrollmentStartNodeId') ? 'border-red-400' : 'border-gray-200 dark:border-gray-600')">
                      <option [value]="null">— Seleccionar paso —</option>
                      @for (node of availableNodes; track node.id) {
                        <option [value]="node.id">{{ getNodeDisplayLabel(node) }}</option>
                      }
                    </select>
                  } @else {
                    <div class="flex items-start gap-2">
                      <p class="text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700/40 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 flex-1">
                        No hay pasos disponibles. Construye el flujo en el builder y vuelve aquí.
                      </p>
                      <button type="button" (click)="loadNodes()"
                              class="shrink-0 px-3 py-2 text-xs border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors">
                        Recargar
                      </button>
                    </div>
                  }
                  @if (isFieldInvalid('reEnrollmentStartNodeId')) {
                    <p class="mt-1 text-xs text-red-500">Debes seleccionar un paso de inicio.</p>
                  }
                </div>
              }
            </div>
          }

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              Máximo de registros dentro del workflow a la vez <span class="text-gray-400">(opcional)</span>
            </label>
            <input type="number" formControlName="maxActiveEnrollments" min="1" placeholder="Sin límite"
                   class="w-full sm:w-40 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"/>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Sin límite si se deja vacío. Es un tope para <strong>todo el workflow</strong>: cuando haya
              esa cantidad de inscripciones en curso, no entrará ningún registro más hasta que alguna
              termine. Un mismo registro nunca puede tener dos inscripciones activas a la vez.
            </p>
          </div>
        </div>

        <!-- 2. Criterios globales de salida -->
        <div class="mb-4 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-100 dark:border-gray-600">
          <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Criterios globales de salida</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mb-3">
            El registro saldrá automáticamente del workflow si cumple estas condiciones durante la ejecución.
          </p>

          @if (exitCriteriaRules.length === 0 && !showAddExitCriteria) {
            <p class="text-xs text-gray-400 dark:text-gray-500 italic mb-3">
              No hay criterios globales de salida configurados.
            </p>
          }

          @if (exitCriteriaRules.length > 0) {
            <div class="space-y-2 mb-3">
              @for (rule of exitCriteriaRules; track $index) {
                <div class="p-3 bg-white dark:bg-gray-700 rounded-lg border"
                     [ngClass]="editingExitCriteriaIndex === $index ? 'border-blue-400 dark:border-blue-600' : 'border-gray-200 dark:border-gray-600'">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span class="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Salida global</span>
                        <p class="text-xs text-gray-800 dark:text-gray-200 font-medium">
                          {{ getRulePreviewText(rule) }}
                        </p>
                      </div>
                      @if (rule.reason) {
                        <p class="text-xs text-gray-400 dark:text-gray-500">Motivo: {{ rule.reason }}</p>
                      }
                    </div>
                    <div class="flex items-center gap-1 shrink-0">
                      <button type="button" (click)="startEditExitCriteria($index)" [disabled]="lockedByActive"
                              class="disabled:opacity-40 disabled:cursor-not-allowed text-xs text-blue-500 hover:text-blue-700 px-2 py-0.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                        Editar
                      </button>
                      <button type="button" (click)="removeExitCriteriaRule($index)" [disabled]="lockedByActive"
                              class="disabled:opacity-40 disabled:cursor-not-allowed text-xs text-red-400 hover:text-red-600 px-2 py-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          }

          @if (showAddExitCriteria) {
            <div class="p-3 bg-white dark:bg-gray-700 rounded-lg border border-blue-200 dark:border-blue-800 mb-3">
              <p class="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2">
                {{ editingExitCriteriaIndex !== null ? 'Editar criterio de salida' : 'Nuevo criterio de salida' }}
              </p>
              @if (draftExitCriteriaError) {
                <p class="text-xs text-red-500 mb-2">{{ draftExitCriteriaError }}</p>
              }
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Tipo de objeto</label>
                  <select [(ngModel)]="draftExitCriteria.objectType" [ngModelOptions]="{standalone: true}"
                          class="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="">(el objeto del workflow)</option>
                    @for (ot of ruleObjectTypeOptions; track ot.value) {
                      <option [value]="ot.value">{{ ot.label }}</option>
                    }
                    @if (draftExitCriteria.objectType && !isEnabledObjectType(draftExitCriteria.objectType)) {
                      <option [value]="draftExitCriteria.objectType">{{ objectTypeText(draftExitCriteria.objectType) }}</option>
                    }
                  </select>
                </div>
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Campo <span class="text-red-500">*</span></label>
                  <app-wf-property-select
                    [value]="draftExitCriteria.field"
                    [options]="propertyOptionsFor(draftExitCriteria.objectType)"
                    placeholder="Ej: status"
                    (valueChange)="draftExitCriteria.field = $event"></app-wf-property-select>
                </div>
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Operador <span class="text-red-500">*</span></label>
                  <select [(ngModel)]="draftExitCriteria.operator" [ngModelOptions]="{standalone: true}"
                          class="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500">
                    @for (op of ruleOperatorOptions(draftExitCriteria.objectType, draftExitCriteria.field, draftExitCriteria.operator); track op.value) {
                      <option [value]="op.value">{{ op.label }}</option>
                    }
                  </select>
                </div>
                @if (valueRequiredForOperator(draftExitCriteria.operator)) {
                  <div>
                    <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Valor <span class="text-red-500">*</span></label>
                    <!-- El editor se adapta a la propiedad (desplegable del catálogo, buscador de
                         registro, número o fecha) Y al operador: «En lista» pide varios valores y
                         «Entre» pide un rango, en vez de un texto con comas que el motor nunca
                         separaba. -->
                    <app-wf-condition-value
                      [operator]="draftExitCriteria.operator" [value]="draftExitCriteria.value"
                      [values]="draftExitCriteria.values" [from]="draftExitCriteria.from" [to]="draftExitCriteria.to"
                      [property]="draftExitCriteria.field"
                      [options]="propertyOptionsFor(draftExitCriteria.objectType)"
                      placeholder="Ej: LOST"
                      (patch)="applyRuleValue(draftExitCriteria, $event)" />
                  </div>
                }
              </div>
              <div class="mb-2">
                <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Motivo (opcional)</label>
                <input type="text" [(ngModel)]="draftExitCriteria.reason" [ngModelOptions]="{standalone: true}"
                       placeholder="Ej: Deal marcado como perdido"
                       class="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"/>
              </div>
              <div class="flex gap-2">
                <button type="button" (click)="confirmAddExitCriteria()"
                        class="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                  {{ editingExitCriteriaIndex !== null ? 'Guardar cambios' : 'Agregar' }}
                </button>
                <button type="button" (click)="cancelEditExitCriteria()"
                        class="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                  Cancelar
                </button>
              </div>
            </div>
          }

          @if (!showAddExitCriteria) {
            <button type="button" (click)="openAddExitCriteria()" [disabled]="lockedByActive"
                    class="disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Agregar criterio de salida
            </button>
          }
        </div>

        <!-- 3. Reglas de supresión -->
        <div class="mb-4 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-100 dark:border-gray-600">
          <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reglas de supresión</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mb-3">
            Los registros que cumplan estas reglas serán excluidos del workflow y no podrán inscribirse.
          </p>

          @if (suppressionRulesArr.length === 0 && !showAddSuppression) {
            <p class="text-xs text-gray-400 dark:text-gray-500 italic mb-3">
              No hay reglas de supresión configuradas.
            </p>
          }

          @if (suppressionRulesArr.length > 0) {
            <div class="space-y-2 mb-3">
              @for (rule of suppressionRulesArr; track $index) {
                <div class="p-3 bg-white dark:bg-gray-700 rounded-lg border"
                     [ngClass]="editingSuppressionIndex === $index ? 'border-blue-400 dark:border-blue-600' : 'border-gray-200 dark:border-gray-600'">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span class="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold"
                              [ngClass]="rule.type === 'OBJECT' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'">
                          {{ rule.type === 'OBJECT' ? 'Objeto específico' : 'Regla por propiedad' }}
                        </span>
                        <p class="text-xs text-gray-800 dark:text-gray-200 font-medium">
                          @if (rule.type === 'OBJECT') {
                            Excluir {{ objectTypeText(rule.objectType) }} #{{ rule.objectId }}
                          } @else {
                            {{ getRulePreviewText(rule) }}
                          }
                        </p>
                      </div>
                      @if (rule.reason) {
                        <p class="text-xs text-gray-400 dark:text-gray-500">Motivo: {{ rule.reason }}</p>
                      }
                    </div>
                    <div class="flex items-center gap-1 shrink-0">
                      <button type="button" (click)="startEditSuppression($index)" [disabled]="lockedByActive"
                              class="disabled:opacity-40 disabled:cursor-not-allowed text-xs text-blue-500 hover:text-blue-700 px-2 py-0.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                        Editar
                      </button>
                      <button type="button" (click)="removeSuppressionRule($index)" [disabled]="lockedByActive"
                              class="disabled:opacity-40 disabled:cursor-not-allowed text-xs text-red-400 hover:text-red-600 px-2 py-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          }

          @if (showAddSuppression) {
            <div class="p-3 bg-white dark:bg-gray-700 rounded-lg border border-blue-200 dark:border-blue-800 mb-3">
              <p class="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2">
                {{ editingSuppressionIndex !== null ? 'Editar regla de supresión' : 'Nueva regla de supresión' }}
              </p>
              @if (draftSuppressionError) {
                <p class="text-xs text-red-500 mb-2">{{ draftSuppressionError }}</p>
              }
              <!-- Tipo de regla -->
              <div class="flex gap-3 mb-3">
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" [(ngModel)]="draftSuppression.type" [ngModelOptions]="{standalone: true}" value="OBJECT"
                         class="w-3.5 h-3.5 border-gray-300 text-blue-600 focus:ring-blue-500"/>
                  <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Por ID de objeto</span>
                </label>
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" [(ngModel)]="draftSuppression.type" [ngModelOptions]="{standalone: true}" value="PROPERTY"
                         class="w-3.5 h-3.5 border-gray-300 text-blue-600 focus:ring-blue-500"/>
                  <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Por propiedad</span>
                </label>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Tipo de objeto <span class="text-red-500">*</span></label>
                  <select [(ngModel)]="draftSuppression.objectType" [ngModelOptions]="{standalone: true}"
                          class="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="">Selecciona…</option>
                    @for (ot of ruleObjectTypeOptions; track ot.value) {
                      <option [value]="ot.value">{{ ot.label }}</option>
                    }
                    @if (draftSuppression.objectType && !isEnabledObjectType(draftSuppression.objectType)) {
                      <option [value]="draftSuppression.objectType">{{ objectTypeText(draftSuppression.objectType) }}</option>
                    }
                  </select>
                </div>

                @if (draftSuppression.type === 'OBJECT') {
                  <div>
                    <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Registro a excluir <span class="text-red-500">*</span></label>
                    @if (suppressionRecordSelect.isSearchable()) {
                      <app-entity-select
                        [value]="draftSuppression.objectId"
                        [displayText]="draftSuppressionObjectName"
                        (valueChange)="draftSuppression.objectId = $event"
                        (selected)="onSuppressionRecordSelected($event)"
                        [searchFn]="suppressionRecordSelect.search" [labelFn]="suppressionRecordSelect.label" [sublabelFn]="suppressionRecordSelect.sublabel"
                        placeholder="Buscar por nombre…"></app-entity-select>
                    } @else {
                      <input type="number" [(ngModel)]="draftSuppression.objectId" [ngModelOptions]="{standalone: true}"
                             placeholder="Ej: 123"
                             class="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"/>
                    }
                  </div>
                }

                @if (draftSuppression.type === 'PROPERTY') {
                  <div>
                    <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Campo <span class="text-red-500">*</span></label>
                    <app-wf-property-select
                      [value]="draftSuppression.field"
                      [options]="propertyOptionsFor(draftSuppression.objectType)"
                      placeholder="Ej: email"
                      (valueChange)="draftSuppression.field = $event"></app-wf-property-select>
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Operador <span class="text-red-500">*</span></label>
                    <select [(ngModel)]="draftSuppression.operator" [ngModelOptions]="{standalone: true}"
                            class="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500">
                      @for (op of ruleOperatorOptions(draftSuppression.objectType, draftSuppression.field, draftSuppression.operator); track op.value) {
                        <option [value]="op.value">{{ op.label }}</option>
                      }
                    </select>
                  </div>
                  @if (valueRequiredForOperator(draftSuppression.operator)) {
                    <div>
                      <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Valor <span class="text-red-500">*</span></label>
                      <app-wf-condition-value
                        [operator]="draftSuppression.operator" [value]="draftSuppression.value"
                        [values]="draftSuppression.values" [from]="draftSuppression.from" [to]="draftSuppression.to"
                        [property]="draftSuppression.field"
                        [options]="propertyOptionsFor(draftSuppression.objectType)"
                        placeholder="Ej: @empresa.com"
                        (patch)="applyRuleValue(draftSuppression, $event)" />
                    </div>
                  }
                }
              </div>
              <div class="mb-2">
                <label class="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Motivo (opcional)</label>
                <input type="text" [(ngModel)]="draftSuppression.reason" [ngModelOptions]="{standalone: true}"
                       placeholder="Ej: Contacto interno"
                       class="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"/>
              </div>
              <div class="flex gap-2">
                <button type="button" (click)="confirmAddSuppression()"
                        class="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                  {{ editingSuppressionIndex !== null ? 'Guardar cambios' : 'Agregar' }}
                </button>
                <button type="button" (click)="cancelEditSuppression()"
                        class="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                  Cancelar
                </button>
              </div>
            </div>
          }

          @if (!showAddSuppression) {
            <button type="button" (click)="openAddSuppression()" [disabled]="lockedByActive"
                    class="disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Agregar regla de supresión
            </button>
          }
        </div>

        <!-- 4. Activación gradual (Ramp-up) -->
        <div class="mb-4 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-100 dark:border-gray-600">
          <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Activación gradual</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mb-3">
            Permite activar el workflow inicialmente solo para un porcentaje de registros elegibles.
          </p>
          <label class="flex items-center gap-2 cursor-pointer mb-3">
            <input type="checkbox" formControlName="rampUpEnabled"
                   class="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"/>
            <span class="text-sm text-gray-700 dark:text-gray-300">Activar ramp-up</span>
          </label>
          @if (f['rampUpEnabled'].value) {
            <div>
              <label class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Porcentaje inicial <span class="text-red-500">*</span>
                <span class="text-gray-400 font-normal ml-1">(1-100)</span>
              </label>
              <div class="flex items-center gap-2">
                <input type="number" formControlName="rampUpPercentage" min="1" max="100" placeholder="Ej: 5"
                       [class]="'w-24 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ' + (isFieldInvalid('rampUpPercentage') ? 'border-red-400' : 'border-gray-200 dark:border-gray-600')"/>
                <span class="text-sm text-gray-500">%</span>
              </div>
              @if (isFieldInvalid('rampUpPercentage')) {
                <p class="mt-1 text-xs text-red-500">El porcentaje debe estar entre 1 y 100.</p>
              }
              @if (f['rampUpPercentage'].value) {
                <p class="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                  El workflow se activará inicialmente para el {{ f['rampUpPercentage'].value }}% de los registros elegibles.
                </p>
              }
            </div>
          }
        </div>

        <!--
          Acciones peligrosas. El backend exige `settings.allowDangerousActions` para ejecutar el
          paso «Eliminar registro»; hasta ahora no había forma de activarlo desde ninguna pantalla,
          así que ese nodo se podía dibujar pero siempre fallaba al ejecutarse.
        -->
        <div class="mb-4 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-100 dark:border-gray-600">
          <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Acciones peligrosas</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mb-3">
            Necesario para que el paso «Eliminar registro» pueda ejecutarse. El borrado es irreversible
            y solo lo puede ejecutar un usuario Admin o superior.
          </p>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" formControlName="allowDangerousActions"
                   class="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"/>
            <span class="text-sm text-gray-700 dark:text-gray-300">Permitir acciones peligrosas (eliminar registros)</span>
          </label>
          @if (f['allowDangerousActions'].value) {
            <p class="mt-2 text-xs text-red-500">
              Este workflow podrá eliminar de forma permanente los registros que inscriba.
            </p>
          }
        </div>

        <!-- 5. Alertas de monitoreo (RF-14) -->
        <div class="mb-4 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-100 dark:border-gray-600">
          <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Alertas de monitoreo</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mb-3">
            Actualmente las alertas se registran en auditoría/log técnico. Notificaciones in-app/email quedan para fase posterior.
          </p>
          <label class="flex items-center gap-2 cursor-pointer mb-3">
            <input type="checkbox" formControlName="monitoringErrorAlertsEnabled"
                   class="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"/>
            <span class="text-sm text-gray-700 dark:text-gray-300">Alertas por errores</span>
          </label>
          @if (f['monitoringErrorAlertsEnabled'].value) {
            <div class="ml-6 space-y-3">
              <div class="flex flex-wrap gap-4">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" formControlName="monitoringNotifyOwner"
                         class="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"/>
                  <span class="text-sm text-gray-600 dark:text-gray-400">Notificar responsable</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" formControlName="monitoringNotifyAdmins"
                         class="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"/>
                  <span class="text-sm text-gray-600 dark:text-gray-400">Notificar administradores</span>
                </label>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Umbral de errores <span class="text-gray-400">(opcional)</span>
                  </label>
                  <input type="number" formControlName="monitoringErrorThreshold" min="1" placeholder="Ej: 5"
                         class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"/>
                  <p class="text-xs text-gray-400 mt-0.5">Alerta cuando se alcance este número de errores.</p>
                </div>
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Umbral de tasa de error % <span class="text-gray-400">(opcional)</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input type="number" formControlName="monitoringErrorRateThreshold" min="1" max="100" placeholder="Ej: 10"
                           class="w-24 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"/>
                    <span class="text-sm text-gray-500">%</span>
                  </div>
                  <p class="text-xs text-gray-400 mt-0.5">Alerta cuando la tasa de error supere este porcentaje.</p>
                </div>
              </div>
            </div>
          }
        </div>

        <!-- Prioridad -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Prioridad <span class="text-gray-400 font-normal">(número menor = mayor prioridad)</span>
          </label>
          <input type="number" formControlName="priorityOrder" min="0"
                 class="w-full sm:w-40 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"/>
        </div>

        </div>
        }

        @if (!isEditing) {
          <div class="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-300 mb-4">
            <svg class="inline w-4 h-4 mr-1 mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            El workflow se creará en estado <strong>Borrador</strong>. Podrás activarlo cuando esté configurado.
          </div>
        }

        <!-- Acciones -->
        <div class="flex gap-3 justify-end pt-2 border-t border-gray-100 dark:border-gray-700">
          <a routerLink="/automation/workflows"
             class="px-4 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors">
            Cancelar
          </a>
          <button type="submit" [disabled]="saving"
                  class="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {{ saving ? 'Guardando...' : (isEditing ? 'Guardar cambios' : 'Crear Workflow (borrador)') }}
          </button>
        </div>

      </form>
    }
  </div>
</div>

<!-- Modal: Plantillas -->
@if (showTemplatesModal) {
  <div class="fixed inset-0 z-modal flex items-center justify-center bg-black/50">
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">
      <div class="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Plantillas de Workflow</h3>
        <button (click)="showTemplatesModal = false"
                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div class="overflow-y-auto p-5 flex-1">
        @if (templatesLoading) {
          <div class="flex justify-center py-8 text-gray-400">
            <svg class="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
          </div>
        } @else if (templates.length === 0) {
          <div class="text-center py-8 text-gray-400">
            <p>No hay plantillas disponibles.</p>
          </div>
        } @else {
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            @for (tpl of templates; track tpl.id) {
              <div class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors"
                   (click)="selectTemplate(tpl)">
                <div class="flex items-start justify-between mb-1">
                  <p class="font-medium text-sm text-gray-900 dark:text-white">{{ tpl.name }}</p>
                  <span class="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded ml-2 shrink-0">
                    {{ objectTypeText(tpl.objectType) }}
                  </span>
                </div>
                @if (tpl.description) {
                  <p class="text-xs text-gray-500 mt-1">{{ tpl.description }}</p>
                }
                @if (tpl.templateCategory) {
                  <p class="text-xs text-amber-600 mt-1">{{ tpl.templateCategory }}</p>
                }
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>
}
